import React from 'react';
import {
    Box,
    Typography,
    Button
} from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import NewTaskButton from '../Tasks/NewTaskButton';
import NewLeadButton from '../Leads/NewLeadButton';
import WidgetCard from './WidgetCard';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useMinIoFileStorageServicePostApiFilesUpload } from '../../api/generated/queries/queries';

const QuickActionsWidget: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const uploadFileMutation = useMinIoFileStorageServicePostApiFilesUpload({
        onSuccess: () => {
            enqueueSnackbar('Plik został przesłany', { variant: 'success' });
            
            // Refresh storage usage in sidebar
            queryClient.invalidateQueries({ queryKey: ['MinIoFileStorageServiceGetApiFilesStorageInfo'] });
        },
        onError: (error) => {
            const msg = error instanceof Error ? error.message : 'Nieznany błąd';
            enqueueSnackbar(`Błąd podczas przesyłania pliku: ${msg}`, { variant: 'error' });
        }
    });

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Automatically upload the selected file
            try {
                await uploadFileMutation.mutateAsync({
                    formData: {
                        file: file
                    }
                });
            } catch (error) {
                console.error('Upload failed:', error);
            }
        }
        // Reset the input value so the same file can be selected again
        event.target.value = '';
    };

    const handleAddFileClick = () => {
        const fileInput = document.getElementById('quick-actions-file-upload-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    };

    return (
        <WidgetCard>
            <Box
                mb={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pb={1}
                borderBottom="1px solid"
                borderColor="divider"
            >
                <Box display="flex" alignItems="center">
                    <Box
                        sx={{
                            backgroundColor: '#ffead8',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1,
                        }}
                    >
                        <FlashOnIcon sx={{ color: 'orange', fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} fontSize="0.95rem">
                        Szybkie akcje
                    </Typography>
                </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
                <NewTaskButton />
                <NewLeadButton />
                <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="quick-actions-file-upload-input"
                    type="file"
                    onChange={handleFileSelect}
                />
                <Button
                    startIcon={<CloudUploadIcon />}
                    onClick={handleAddFileClick}
                    disabled={uploadFileMutation.status === 'pending'}
                    sx={{
                        background: 'linear-gradient(to right, #06b6d4, #2563eb)',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '9999px',
                        fontWeight: 400,
                        textTransform: 'none',
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                        '&:hover': {
                            background: 'linear-gradient(to right, #059dc5, #1e4ed8)',
                        },
                        '&:disabled': {
                            background: '#9ca3af',
                            color: '#ffffff',
                        },
                    }}
                >
                    {uploadFileMutation.status === 'pending' ? 'Przesyłanie...' : 'Dodaj plik'}
                </Button>
            </Box>
        </WidgetCard>
    );
};

export default QuickActionsWidget;

